import { computed, ref } from 'vue'

import { SCAN_FIRST_RUN_NOTE } from 'src/services/consts/scan/scan.const'

const confirmModalData = ref(null)
const isOpen = computed(() => !!confirmModalData.value)

export function useConfirm() {
  const showConfirm = ({
    title = 'Unsaved changes',
    message = "Your changes haven't been saved and will be lost.",
    icon = 'alert-triangle',
    confirmText = 'Discard',
    cancelText = 'Go back',
    continueText = null,
    confirmColor = 'brand',
    cancelColor = 'brand-light',
    continueColor = 'primary',
    onConfirm = () => {},
    onCancel = () => {},
    onContinue = null,
  } = {}) => {
    confirmModalData.value = {
      title,
      message,
      icon,
      confirmText,
      cancelText,
      continueText,
      confirmColor,
      cancelColor,
      continueColor,
      onConfirm,
      onCancel,
      onContinue,
    }
  }

  const closeConfirm = () => {
    confirmModalData.value = null
  }

  // Common confirmation modal presets
  const confirmDelete = (itemName = 'item', onConfirm) => {
    showConfirm({
      title: `Delete ${itemName}`,
      message: `This ${itemName} will be permanently removed.`,
      icon: 'trash',
      confirmText: 'Delete',
      cancelText: 'Keep it',
      onConfirm,
    })
  }

  /** Promise wrapper for route/tab guards — resolves true when user discards, false on cancel. */
  const confirmDiscardUnsavedChanges = ({ onDiscard } = {}) => {
    return new Promise((resolve) => {
      showConfirm({
        title: 'Unsaved changes',
        message: "Your changes haven't been saved and will be lost.",
        icon: 'alert-triangle',
        confirmText: 'Discard',
        cancelText: 'Go back',
        onConfirm: () => {
          onDiscard?.()
          resolve(true)
        },
        onCancel: () => {
          resolve(false)
        },
      })
    })
  }

  /** Promise wrapper for scan confirmation — resolves true on confirm, false on cancel. */
  const confirmScan = ({ isFirstScan = false } = {}) => {
    return new Promise((resolve) => {
      showConfirm({
        title: 'Run a new scan',
        message: isFirstScan
          ? SCAN_FIRST_RUN_NOTE
          : 'This will scan your website for cookies. It may take a few minutes.',
        icon: 'scan-search',
        confirmText: 'Run scan',
        cancelText: 'Cancel',
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      })
    })
  }

  /** Promise wrapper for domain-change save+scan — resolves true on confirm, false on cancel. */
  const confirmUrlChangeScan = () => {
    return new Promise((resolve) => {
      showConfirm({
        title: 'Domain change - new scan required',
        message: 'Changing the domain will start a new scan. Do you want to save and scan?',
        icon: 'scan-search',
        confirmText: 'Save & scan',
        cancelText: 'Cancel',
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      })
    })
  }

  return {
    isOpen,
    confirmModalData,
    showConfirm,
    closeConfirm,
    // Preset helpers
    confirmDelete,
    confirmDiscardUnsavedChanges,
    confirmScan,
    confirmUrlChangeScan,
  }
}
